import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { FollowButton } from '@/features/subscriber/FollowButton';
import { useAuth } from '@/lib/auth/useAuth';
import { subscriberQueries } from '@/lib/queries/subscriber';

export const Route = createFileRoute('/subscriber/contributors')({
  component: ContributorsRoute,
});

function ContributorsRoute() {
  const { user } = useAuth();
  const creators = useQuery(subscriberQueries.creators());
  const following = useQuery(subscriberQueries.following(user?.id));
  const [tab, setTab] = useState<'following' | 'browse'>('browse');

  const all = creators.data ?? [];
  const followedIds = new Set(following.data ?? []);
  const list = tab === 'following' ? all.filter((c) => followedIds.has(c.id)) : all;

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="font-serif text-3xl text-ink">Contributors</h1>

      <div className="mt-6 flex gap-1 border-b border-ink/10">
        {(
          [
            ['browse', 'Browse'],
            ['following', 'Following'],
          ] as const
        ).map(([val, lbl]) => (
          <button
            key={val}
            type="button"
            onClick={() => setTab(val)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${
              tab === val
                ? 'border-oxblood text-ink'
                : 'border-transparent text-ink/60 hover:text-ink'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <p className="mt-12 text-center font-sans text-sm text-ink/50">
          {tab === 'following' ? "You aren't following anyone yet." : 'No contributors yet.'}
        </p>
      ) : (
        <ul className="mt-8 divide-y divide-ink/10">
          {list.map((c) => (
            <li key={c.id} className="flex items-center gap-4 py-4">
              {c.avatar_url ? (
                <img src={c.avatar_url} alt="" className="h-12 w-12 rounded-full object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ink/10 font-serif text-ink/50">
                  {c.display_name?.[0]?.toUpperCase() ?? '·'}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <Link
                  to="/subscriber/creators/$username"
                  params={{ username: c.username ?? '' }}
                  className="font-serif text-lg text-ink hover:text-oxblood"
                >
                  {c.display_name}
                </Link>
                {c.bio && <p className="line-clamp-1 font-sans text-sm text-ink/60">{c.bio}</p>}
              </div>
              <FollowButton creatorId={c.id} size="sm" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
